import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'

export type StaffRole = 'admin' | 'contador' | 'backoffice'

type AuthState = {
  session: Session | null
  loading: boolean
  /** true si el JWT del usuario actual trae `app_metadata.role === 'admin'`. */
  isAdmin: boolean
  /** role staff del usuario actual (admin / contador / backoffice) o null si no es staff. */
  role: StaffRole | null
  signIn: (email: string, password: string) => Promise<{ error?: string }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

/**
 * Lee el claim `role` del JWT o de app_metadata. Ambos existen por
 * redundancia: el claim viene si lo copiamos al access_token via trigger,
 * pero en Supabase default el role queda solo en app_metadata del user.
 */
function detectRole(session: Session | null): StaffRole | null {
  if (!session) return null
  const raw = session.user?.app_metadata?.role
  if (raw === 'admin' || raw === 'contador' || raw === 'backoffice') {
    return raw
  }
  return null
}

function detectAdmin(session: Session | null): boolean {
  // El contador tiene todos los privilegios del administrador.
  const r = detectRole(session)
  return r === 'admin' || r === 'contador'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return
      setSession(data.session)
      setLoading(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return
      setSession(s)
    })

    return () => {
      mounted = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    })
    if (error) return { error: error.message }
    return {}
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      session,
      loading,
      isAdmin: detectAdmin(session),
      role: detectRole(session),
      signIn,
      signOut,
    }),
    [session, loading, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>.')
  return ctx
}
